import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Navigate } from 'react-router';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import CheckAccess from '../../components/check-access/check-access.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../components/form/form.component';
import FormContainer from '../../components/form-container/form-container.component';
import FormError from '../../components/form-error/form-error.component';
import FilterPanel from '../../components/filter-panel/filter-panel.component';
import ControlButtonBlock from '../app/common/control-button-block/control-button-block.component';
import Controls from '../app/controls/controls.component';
import PortfolioFilter from '../app/controls/portfolio-filter/portfolio-filter.component';
import StatusFilter from '../app/controls/status-filter/status-filter.component';
import ExportModal from '../app/controls/export-modal/export-modal.component';
import StatusModal from '../app/controls/status-modal/status-modal.component';
import ActivityListInternal from './activity-list-internal/activity-list-internal.component';
import ActivityModal from './activity-modal/activity-modal.component';
import ActivityListModal from './activity-list-modal/activity-list-modal.component';
import ActivityListFilters from './activity-list-filters/activity-list-filters.component';
import ActivityListValidator from './activity-list.validator';
import FormatService from '../../services/format-service';
import StructureService from '../../services/structure-service';
import FormService from '../../services/form-service';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useScreenSmall from '../../hooks/use-screen-small';
import useScreenMobile from '../../hooks/use-screen-mobile';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import setStatusFilter from '../app/controls/status-filter/status-filter.action';
import {
  getActivityListData, deleteActivityList, saveActivityListData, resetActivityListData, saveActivityStatusList,
  saveActivityList,
} from './activity-list.action';
import { deleteActivityComment, saveActivityComment } from '../activity/activity.action';
import {
  toggleEditMode, toggleExportMode, toggleCustomizeMode, toggleSelectMode, saveTableColumnSettings,
} from '../app/controls/controls.action';
import { setPortfolioIdsFilter, setPortfolioGroupIdsFilter }
  from '../app/controls/portfolio-filter/portfolio-filter.action';
import {
  getListActivityDealData, getListContactCompanyData, getListContactData, getListContactPersonData,
  getListContactPersonInternalData, getListDealAssetData, getListTagData,
} from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { FORM_ID_ACTIVITY_LIST } from '../../constants/system/form-ids';
import { FORMAT_LIST_IMPORT_ACTIVITY, FORMAT_LIST_EXPORT_ACTIVITY } from '../../constants/system/format-lists';
import {
  ACTIVITY_CREATE, ACTIVITY_DELETE, ACTIVITY_EDIT, ACTIVITY_LIST_EXPORT, ACTIVITY_LIST_IMPORT, ACTIVITY_LIST_VIEW,
  ACTIVITY_PORTFOLIO_FILTER_VIEW, ACTIVITY_STATUS_EDIT, ACTIVITY_STATUS_FILTER_VIEW,
} from '../../constants/entity/permissions';
import { PAGE_DEFAULT } from '../../constants/system/pages';
import { TABLE_LIMIT_DEFAULT } from '../../constants/system/table-limits';
import { FILTER_VALUE_EMPTY } from '../../constants/system/filter-values';
import { SORT_TYPE_ASC } from '../../constants/system/sort-types';
import { ROUTE_SEARCH, ROUTE_ACTIVITY_LIST } from '../../constants/system/routes';
import {
  QUERY_PARAM_COMMENT_ID, QUERY_PARAM_CATEGORY, QUERY_PARAM_TYPE, QUERY_PARAM_STATUS,
  QUERY_PARAM_TAG_ID, QUERY_PARAM_PAGE, QUERY_PARAM_LIMIT, QUERY_PARAM_DEAL_ID, QUERY_PARAM_BROKER_ID,
  QUERY_PARAM_LENDER_ID, QUERY_PARAM_INVESTOR_ID, QUERY_PARAM_DEAL_CONTACT_ID,
  QUERY_PARAM_PORTFOLIO_IDS, QUERY_PARAM_PORTFOLIO_GROUP_IDS, QUERY_PARAM_CREATE,
} from '../../constants/system/query-params';
import {
  FILTER_TYPE_DEAL_ID, FILTER_TYPE_BROKER_ID, FILTER_TYPE_LENDER_ID, FILTER_TYPE_INVESTOR_ID,
  FILTER_TYPE_DEAL_CONTACT_ID, FILTER_TYPE_TYPE,
} from '../../constants/system/filter-types';
import { TABLE_NAME_ACTIVITY_LIST_CAPITAL_RAISE } from '../../constants/system/table-names';
import { IMPORT_EXPORT_ENTITY_TYPE_ACTIVITY_LIST } from '../../constants/system/import-export-entity-types';
import { FILTER_KEY_NAME } from '../../constants/system/filter-keys';
import { FILTER_LABEL_KEY_FULL_NAME } from '../../constants/system/filter-label-keys';
import { ACTIVITY_CATEGORY_CAPITAL_RAISE } from '../../constants/entity/activity-categories';
import { ACTIVITY_STATUS_INITIAL } from '../../constants/entity/activity-statuses';
import { SIGN_DOT } from '../../constants/system/signs';
import ACTIVITY_TYPE_DEBT from '../../constants/entity/activity-types';

const ActivityList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDataLoaded,
    isExportMode,
    isEditMode,
    isSelectMode,
    isCustomizeMode,
    isRightDrawerOpen,
    isCommentSaving,
    currentUser,
    statusFilterStatus,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    newCommentList,
    portfolioGroupList,
    activityListData,
    activityTotal,
    activityListSummaryData,
    activityStatusList,
    activityCategoryList,
    measureUnitList,
    activityInterestLevelList,
    activityTypeList,
    assetList,
    filterDealList,
    filterActivityTypeList,
    filterBrokerList,
    filterLenderList,
    filterInvestorList,
    filterDealContactList,
    dealList,
    contactList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    contactGroupList,
    tagList,
    contactLinkedUserList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    initialValues: initialFormValues,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const dispatch: ReduxDispatch = useDispatch();
  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const isMobileScreen: boolean = useScreenMobile();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const permissionList: string[] = useCurrentUserPermissions();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryCreate: boolean | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CREATE]
    ? FormatService.extractBooleanValue(locationQueryParams[QUERY_PARAM_CREATE].toString()) : null;
  const queryCommentId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMMENT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMMENT_ID].toString()) : null;
  const queryPage: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_PAGE]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_PAGE].toString()) : null;
  const queryLimit: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LIMIT]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_LIMIT].toString()) : null;
  const queryFilterDealId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_DEAL_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_DEAL_ID].toString()) : null;
  const queryFilterBrokerId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_BROKER_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_BROKER_ID].toString()) : null;
  const queryFilterLenderId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LENDER_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_LENDER_ID].toString()) : null;
  const queryFilterInvestorId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_INVESTOR_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_INVESTOR_ID].toString()) : null;
  const queryFilterDealContactId: number | null = locationQueryParams
  && locationQueryParams[QUERY_PARAM_DEAL_CONTACT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_DEAL_CONTACT_ID].toString()) : null;
  const queryFilterType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TYPE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TYPE].toString()) : null;
  const queryCategory: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CATEGORY]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_CATEGORY].toString()) : null;
  const queryFilterStatus: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_STATUS]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_STATUS].toString()) : null;
  const queryFilterPortfolioIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS].toString().split(SIGN_DOT)
        .map((portfolioId: string): number => FormatService.parseInt(portfolioId)) : null
  );
  const queryFilterPortfolioGroupIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS].toString().split(SIGN_DOT)
        .map((portfolioGroupId: string): number => FormatService.parseInt(portfolioGroupId)) : null
  );

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'deal.name',
    type: SORT_TYPE_ASC,
  }), []);

  const DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_DEAL_ID]: null,
    [FILTER_TYPE_TYPE]: null,
    [FILTER_TYPE_BROKER_ID]: null,
    [FILTER_TYPE_LENDER_ID]: null,
    [FILTER_TYPE_INVESTOR_ID]: null,
    [FILTER_TYPE_DEAL_CONTACT_ID]: null,
  }), []);

  const FILTER_OPTION_MAP: Record<string, FilterOption[]> = useMemo((): Record<string, FilterOption[]> => ({
    [FILTER_TYPE_DEAL_ID]: filterDealList,
    [FILTER_TYPE_TYPE]: filterActivityTypeList,
    [FILTER_TYPE_BROKER_ID]: filterBrokerList,
    [FILTER_TYPE_LENDER_ID]: filterLenderList,
    [FILTER_TYPE_INVESTOR_ID]: filterInvestorList,
    [FILTER_TYPE_DEAL_CONTACT_ID]: filterDealContactList,
  }), [
    filterDealList, filterActivityTypeList, filterBrokerList, filterLenderList,
    filterInvestorList, filterDealContactList,
  ]);

  const FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_TYPE]: FILTER_KEY_NAME,
  }), []);

  const FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_DEAL_ID]: 'Deal',
    [FILTER_TYPE_TYPE]: 'Type',
    [FILTER_TYPE_BROKER_ID]: 'Broker',
    [FILTER_TYPE_LENDER_ID]: 'Lender',
    [FILTER_TYPE_INVESTOR_ID]: 'Investor',
    [FILTER_TYPE_DEAL_CONTACT_ID]: 'Deal Contact',
  }), []);

  const FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_DEAL_CONTACT_ID]: FILTER_LABEL_KEY_FULL_NAME,
  }), []);

  const activityListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_LIST_VIEW], []);
  const activityPortfolioFilterViewPermissions: string[] = useMemo((): string[] => [
    ACTIVITY_PORTFOLIO_FILTER_VIEW,
  ], []);
  const activityStatusFilterViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_STATUS_FILTER_VIEW], []);
  const activityEditPermissions: string[] = useMemo((): string[] => [ACTIVITY_EDIT], []);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const [isActivityModalOpen, setIsActivityModalOpen]: [boolean, Function] = useState(false);
  const [isActivityListModalOpen, setIsActivityListModalOpen]: [boolean, Function] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen]: [boolean, Function] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isStatusChangeModalOpen, setIsStatusChangeModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [activityItemData, setActivityItemData]: [Activity | null, Function] = useState<StateValueDefault>(null);
  const [isCommentFormActive, setIsCommentFormActive]: [boolean, Function] = useState(false);
  const [selectedRows, setSelectedRows]: [number[], Function] = useState([]);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [activityActiveIds, setActivityActiveIds]: [number[], Function] = useState([]);
  const [page, setPage]: [number, Function] = useState(PAGE_DEFAULT);
  const [limit, setLimit]: [number, Function] = useState(TABLE_LIMIT_DEFAULT);
  const [isNewActivity, setIsNewActivity]: [boolean, Function] = useState(false);
  const [isNewDealFormVisible, setIsNewDealFormVisible]: [boolean, Function] = useState(false);
  const [isNewDealSourceFormVisible, setIsNewDealSourceFormVisible]: [boolean, Function] = useState(false);
  const [isNewDealContactFormVisible, setIsNewDealContactFormVisible]: [boolean, Function] = useState(false);
  const [dealPhotoFiles, setDealPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [currentCategory, setCurrentCategory]: [string | null, Function] = useState(null);
  const [activityFieldNewItems, setActivityFieldNewItems]: [Record<string, ActivityFieldInput[]>, Function]
    = useState({});
  const [categoryText, setCategoryText]: [string, Function] = useState('');
  const [statusFilterText, setStatusFilterText]: [string, Function] = useState('');
  const [sortParams, setSortParams]: [SortParams, Function] = useState(DEFAULT_SORT);
  const [activityTableColumnSettings, setActivityTableColumnSettings]: [TableField[] | null, Function] = useState(null);
  const [withAllRowsSelected, setWithAllRowsSelected]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastStatusValue, setLastStatusValue]: [string | null, Function] = useState(null);
  const [lastPortfolioIdsValue, setLastPortfolioIdsValue]: [number[] | null, Function] = useState(null);
  const [lastPortfolioGroupIdsValue, setLastPortfolioGroupIdsValue]: [number[] | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [filters, setFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [currentFilters, setCurrentFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [statusModalErrorMessage, setStatusModalErrorMessage]: [string | null, Function] = useState(null);
  const [inlineSaveActivityId, setInlineSaveActivityId]: [number | null, Function] = useState(null);
  const [inlineSaveIsConfirm, setInlineSaveIsConfirm]: [boolean | null, Function] = useState(null);
  const [activityListModalErrorMessage, setActivityListModalErrorMessage]: [string | null, Function] = useState(null);

  const pageTitle: string = `${categoryText ? `${categoryText} ` : ''}Activity`
    + `${statusFilterText ? `: ${statusFilterText}` : ''}`;
  const createButtonTitle: string | null = !isExportMode && !isSelectMode && !isCustomizeMode ? 'New Activity' : null;
  const createAdditionalItemButtonTitle: string | null = !isExportMode && !isSelectMode && !isCustomizeMode
    ? 'New Activities' : null;
  const isCustomizable: boolean = !isSmallScreen;
  const isFormDataPristine: boolean = isFormPristine && !Object.keys(dealPhotoFiles).length
    && !Object.keys(activityFieldNewItems).length;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isExportMode || isSelectMode || isCustomizeMode
    || !!statusFilterStatus;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isExportDisabled: boolean = !selectedRows || !selectedRows.length;
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && !!lastLocation;
  const withAllFiltersEmpty: boolean = !filters[FILTER_TYPE_BROKER_ID] && !filters[FILTER_TYPE_DEAL_CONTACT_ID]
    && !filters[FILTER_TYPE_DEAL_ID] && !filters[FILTER_TYPE_INVESTOR_ID]
    && !filters[FILTER_TYPE_LENDER_ID] && !filters[FILTER_TYPE_TYPE];
  const filteredPortfolioGroupList: PortfolioGroup[] = portfolioGroupList
    && portfolioGroupList.filter((portfolioGroupItem: PortfolioGroup): boolean => (
      !!portfolioGroupItem.portfolios.length
    ));
  const activityItemId: number = (activityItemData && activityItemData.id) || FormService.FORM_KEY_NEW;
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;
  const isStatusFilterVisible: boolean = activityStatusList && !!activityStatusList.length
    && StructureService.checkPermission(ACTIVITY_STATUS_FILTER_VIEW, permissionList);
  const isPortfolioFilterViewAllowed: boolean = StructureService.checkPermission(
    ACTIVITY_PORTFOLIO_FILTER_VIEW, permissionList,
  );
  const deleteConfirmModalTitle: string = activityActiveIds && activityActiveIds.length > 1
    ? 'Delete Activities' : 'Delete Activity';
  const deleteConfirmModalText: string = activityActiveIds && activityActiveIds.length > 1
    ? 'Are you sure you want to delete these activities?'
    : 'Are you sure you want to delete this activity?';

  const statusFilterList: ActivityStatus[] = useMemo((): ActivityStatus[] => (
    activityStatusList && activityStatusList.length
      ? activityStatusList.map((activityStatusItem: ActivityStatus, activityStatusIndex: number): StatusFilterItem => ({
        id: activityStatusIndex + 1,
        name: activityStatusItem.name,
        title: activityStatusItem.title,
        itemsTotal: activityStatusItem.activitiesTotal,
      })) : []
  ), [activityStatusList]);

  const activityListTableFieldLabels: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'status',
      title: 'Status',
      group: 'generalInformation',
    },
    {
      id: 2,
      name: 'type',
      title: 'Type',
      group: 'generalInformation',
    },
    {
      id: 3,
      name: 'deal.name',
      title: 'Deal',
      group: 'generalInformation',
    },
    {
      id: 4,
      name: 'dealSource.name',
      title: 'Deal Source',
      group: 'generalInformation',
    },
    {
      id: 5,
      name: 'sponsor.fullName',
      title: 'Sponsor',
      group: 'generalInformation',
    },
    {
      id: 6,
      name: 'broker.fullName',
      title: 'Broker',
      group: 'generalInformation',
    },
    {
      id: 7,
      name: 'lender.fullName',
      title: 'Lender',
      group: 'generalInformation',
    },
    {
      id: 8,
      name: 'amount',
      title: 'Total Capitalization',
      group: 'generalInformation',
    },
    {
      id: 9,
      name: 'pricing',
      title: 'Price Guidance',
      group: 'generalInformation',
    },
    {
      id: 10,
      name: 'size',
      title: 'Size',
      group: 'generalInformation',
    },
    {
      id: 11,
      name: 'dealContacts.fullName',
      title: 'Deal Contacts',
      group: 'generalInformation',
    },
    {
      id: 12,
      name: 'dealContacts.phone',
      title: 'Deal Contact Phones',
      group: 'generalInformation',
    },
    {
      id: 13,
      name: 'dealContacts.email',
      title: 'Deal Contact Emails',
      group: 'generalInformation',
    },
    {
      id: 14,
      name: 'purchasePrice',
      title: 'Purchase Price',
      group: 'generalInformation',
    },
    {
      id: 15,
      name: 'capitalExpenditure',
      title: 'CapEx',
      group: 'generalInformation',
    },
    {
      id: 16,
      name: 'oneYearNoi',
      title: '1 Year NOI',
      group: 'investmentInformation',
    },
    {
      id: 17,
      name: 'yieldOnCost',
      title: 'Yield On Cost',
      group: 'investmentInformation',
    },
    {
      id: 18,
      name: 'debtProceeds',
      title: 'Debt Proceeds',
      group: 'investmentInformation',
    },
    {
      id: 19,
      name: 'requiredEquityAmount',
      title: 'Required Equity',
      group: 'investmentInformation',
    },
    {
      id: 20,
      name: 'interestLevel',
      title: 'Level of Interest',
      group: 'investmentInformation',
    },
    {
      id: 21,
      name: 'ltc',
      title: 'LTC',
      group: 'investmentInformation',
    },
    {
      id: 22,
      name: 'equityProvider.fullName',
      title: 'Equity Provider',
      group: 'investmentInformation',
    },
    {
      id: 23,
      name: 'investor.fullName',
      title: 'Investor',
      group: 'investmentInformation',
    },
    {
      id: 24,
      name: 'lenderRole',
      title: 'Lender Role',
      group: 'investmentInformation',
    },
    {
      id: 25,
      name: 'maxAmount',
      title: 'Max Amount',
      group: 'investmentInformation',
    },
    {
      id: 26,
      name: 'loanRecourse',
      title: 'Loan Recourse',
      group: 'investmentInformation',
    },
    {
      id: 27,
      name: 'additionalParty.fullName',
      title: 'Additional Party',
      group: 'investmentInformation',
    },
    {
      id: 28,
      name: 'tags',
      title: 'Tags',
      group: 'other',
    },
    {
      id: 29,
      name: 'updatedDate',
      title: 'Last Update',
      group: 'other',
    },
  ], []);

  const tableActivityListFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'generalInformation',
      title: 'General Information',
    },
    {
      id: 2,
      name: 'investmentInformation',
      title: 'Investment Information',
    },
    {
      id: 3,
      name: 'other',
      title: 'Other',
    },
  ], []);

  const onActivityDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListActivityDealData({
      query,
    }));
  }, []);

  const onActivityDealSearchClose: Function = useCallback((): void => {
    dispatch(getListActivityDealData({
      query: null,
    }));
  }, []);

  const onDealAssetSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealAssetData({
      query,
    }));
  }, []);

  const onDealAssetSearchClose: Function = useCallback((): void => {
    dispatch(getListDealAssetData({
      query: null,
    }));
  }, []);

  const onContactSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactData({
      query,
    }));
  }, []);

  const onContactSearchClose: Function = useCallback((): void => {
    dispatch(getListContactData({
      query: null,
    }));
  }, []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonData({
      query,
    }));
  }, []);

  const onContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonData({
      query: null,
    }));
  }, []);

  const onContactPersonInternalSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonInternalData({
      query,
    }));
  }, []);

  const onContactPersonInternalSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonInternalData({
      query: null,
    }));
  }, []);

  const onCommentEntityLinkClick: Function = useCallback((link: string): void => {
    navigate(link);
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    if (inlineSaveActivityId !== null) {
      if (isActivityModalOpen) {
        closeActivityModal();
      }

      inlineCancel();
    } else {
      if (isActivityModalOpen) {
        closeActivityModal();
      }

      FormService.forceResetForm(resetForm, formProps);

      setSelectedRows([]);
      setWithAllRowsSelected(false);
      setDealPhotoFiles({});
      setActivityFieldNewItems({});
      setActivityActiveIds([]);
      setActivityItemData(null);
      setIsNewDealContactFormVisible(false);
      setIsNewDealSourceFormVisible(false);
      setIsNewDealFormVisible(false);
      setInlineSaveActivityId(null);
      setInlineSaveIsConfirm(null);

      if (!isNewActivity) {
        dispatch(toggleEditMode(false));
        dispatch(toggleCustomizeMode(false));
        dispatch(toggleExportMode(false));
        dispatch(toggleSelectMode(false));
      }
    }
  }, [
    isNewActivity, inlineSaveActivityId, activityActiveIds, isEditMode, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const getOptions: Function = useCallback((
    pageOption: number,
    limitOption: number,
    portfolioIdsOption?: number[],
    portfolioGroupIdsOption?: number[],
    statusOption?: string,
    categoryOption?: string,
    sortOption?: SortParams,
    filtersOption?: Record<string, FilterValue>,
  ): RequestOptions => {
    let options: RequestOptions = { page: pageOption + 1, limit: limitOption };

    options = portfolioIdsOption && portfolioIdsOption.length
      ? { ...options, portfolioIds: portfolioIdsOption } : options;
    options = portfolioGroupIdsOption && portfolioGroupIdsOption.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsOption } : options;
    options = statusOption ? { ...options, status: statusOption } : options;
    options = categoryOption ? { ...options, category: categoryOption } : options;
    options = sortOption ? { ...options, sort: sortOption } : options;
    options = filtersOption ? { ...options, filters: { ...filtersOption } } : options;

    return options;
  }, []);

  const updateQueryParams: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (queryPage) {
      queryParams[QUERY_PARAM_PAGE] = queryPage + 1;
    }

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [
    locationQueryParams, queryCategory, queryLimit, queryPage, filters, statusFilterStatus,
    portfolioIdsFilter, portfolioGroupIdsFilter,
  ]);

  const onPageChange: Function = useCallback((pageValue: number): void => {
    setPage(pageValue);

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    queryParams[QUERY_PARAM_PAGE] = pageValue + 1;

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [
    locationQueryParams, queryCategory, queryLimit, filters, statusFilterStatus,
    portfolioIdsFilter, portfolioGroupIdsFilter,
  ]);

  const onLimitChange: Function = useCallback((limitValue: number): void => {
    setPage(PAGE_DEFAULT);
    setLimit(limitValue);

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    queryParams[QUERY_PARAM_LIMIT] = limitValue;

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryCategory, filters, statusFilterStatus, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onSortParamsChange: Function = useCallback((sortField: string, sortType: SortType): void => {
    const sortParamsValue: SortParams = {
      field: sortField,
      type: sortType,
    };

    setPage(PAGE_DEFAULT);
    setSortParams(sortParamsValue);

    dispatch(getActivityListData(
      getOptions(
        PAGE_DEFAULT,
        limit,
        portfolioIdsFilter,
        portfolioGroupIdsFilter,
        statusFilterStatus,
        queryCategory,
        sortParamsValue,
        filters,
      ),
    ));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [
    locationQueryParams, limit, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus,
    queryCategory, filters, queryCategory, queryLimit,
  ]);

  const onResetFilters: Function = useCallback((): void => {
    setCurrentFilters({ ...filters });
  }, [filters]);

  const onApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || currentFilters;

    setFilters({ ...resultNewFilters });
    setPage(PAGE_DEFAULT);

    dispatch(getActivityListData({
      ...getOptions(
        PAGE_DEFAULT,
        limit,
        portfolioIdsFilter,
        portfolioGroupIdsFilter,
        statusFilterStatus,
        queryCategory,
        sortParams,
        resultNewFilters,
      ),
    }));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (newFilters) {
      Object.entries(newFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [
    locationQueryParams, currentFilters, limit, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus,
    queryCategory, sortParams, queryCategory, queryLimit,
  ]);

  const removeCreateQueryParam: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    delete queryParams[QUERY_PARAM_CREATE];

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams]);

  const openActivityModal: Function = useCallback((
    activityItem: Activity, isCommentActive?: boolean, isNew?: boolean,
  ): void => {
    setActivityItemData({ ...activityItem });
    setIsActivityModalOpen(true);

    if (isCommentActive) {
      setIsCommentFormActive(isCommentActive);
    }

    if (isNew) {
      setIsNewActivity(isNew);
    }

    if (isEditMode) {
      setInlineSaveIsConfirm(null);
      setInlineSaveActivityId(FormService.FORM_KEY_NEW);
    }
  }, [isEditMode]);

  const closeActivityModal: Function = useCallback((): void => {
    setIsActivityModalOpen(false);
    setIsCommentFormActive(false);
    setActivityItemData(null);
    setIsNewActivity(false);

    if (isEditMode) {
      setInlineSaveActivityId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, activityActiveIds]);

  const openActivityListModal: Function = useCallback((): void => {
    setIsActivityListModalOpen(true);
  }, []);

  const closeActivityListModal: Function = useCallback((): void => {
    setIsActivityListModalOpen(false);
    setActivityListModalErrorMessage(null);
  }, []);

  const onSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
      setLastStatusValue(undefined);
      setLastPortfolioIdsValue(undefined);
      setLastPortfolioGroupIdsValue(undefined);

      return false;
    }

    if (!isNewActivity) {
      if (isEditMode) {
        dispatch(toggleEditMode(false));
      }
      if (isExportMode) {
        dispatch(toggleExportMode(false));
      }
      if (isSelectMode) {
        dispatch(toggleSelectMode(false));
      }
      if (isCustomizeMode) {
        dispatch(toggleCustomizeMode(false));
      }
    }

    dispatch(setStatusFilter(undefined));

    return true;
  }, [isFormChanged, isNewActivity, isEditMode, isExportMode, isSelectMode, isCustomizeMode]);

  const onInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityId: number,
  ): void => {
    setActivityActiveIds([...(activityActiveIds || []), activityId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, activityActiveIds]);

  const onTagsChange: Function = useCallback((activityId: number, tags: Tag[]): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].tags`, tags);
  }, []);

  const onDealContactsChange: Function = useCallback((activityId: number, contacts: Contact[]): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealContactIds`, contacts);
  }, []);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onTagClick: Function = useCallback((event: React.ChangeEvent, tagId: number): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const checkIsActivityActive: Function = useCallback((activityId: number): boolean => (
    activityActiveIds ? activityActiveIds.includes(activityId) : false
  ), [activityActiveIds]);

  const inlineCancel: Function = useCallback((): void => {
    const activityId: number | null = inlineSaveActivityId;

    if (activityId) {
      const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

      if (initialFormValues && initialFormValues.activityListData) {
        changeForm(`activityListData[${formKeyActivity}]`, initialFormValues.activityListData[formKeyActivity]);
      }

      if (activityId !== FormService.FORM_KEY_NEW) {
        const activityActiveIdIndex: number = activityActiveIds.findIndex((activityItemActiveId: number): boolean => (
          activityItemActiveId === activityId
        ));

        if (activityActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newActivityActiveIds: number[] = [...activityActiveIds];
          StructureService.deleteListItemByIndex(newActivityActiveIds, activityActiveIdIndex);
          setActivityActiveIds(newActivityActiveIds);
        }
      }

      if (dealPhotoFiles[activityId]) {
        const newDealPhotoFiles: Record<string, File> = { ...dealPhotoFiles };
        delete newDealPhotoFiles[activityId];
        setDealPhotoFiles(newDealPhotoFiles);
      }

      if (activityFieldNewItems[activityId]) {
        const newActivityFieldNewItems: Record<string, ActivityFieldInput[]> = { ...activityFieldNewItems };
        delete newActivityFieldNewItems[activityId];
        setActivityFieldNewItems(newActivityFieldNewItems);
      }
    }

    setIsNewDealContactFormVisible(false);
    setIsNewDealSourceFormVisible(false);
    setIsNewDealFormVisible(false);
    setInlineSaveIsConfirm(null);
    setInlineSaveActivityId(null);
  }, [initialFormValues, activityActiveIds, dealPhotoFiles, activityFieldNewItems, inlineSaveActivityId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    let formData: FormValues = {};

    if (isNewActivity) {
      if (values.activityListData[formKeyNew]) {
        formData[formKeyNew] = {
          ...values.activityListData[formKeyNew],
        };
      }
    } else {
      formData = {
        ...values.activityListData,
      };
    }

    if (!Object.keys(formData).length) {
      return null;
    }

    const activityList: ActivityInput[] = [];
    Object.keys(formData).forEach((activityIndex: string): void => {
      const activityFormItem: FormValueItem = {
        ...formData[activityIndex],
      };

      if (activityFormItem && (isNewActivity || (inlineSaveActivityId && inlineSaveActivityId === activityFormItem.id)
        || (!inlineSaveActivityId && checkIsActivityActive(activityFormItem.id)))) {
        if (activityFormItem.deal && Object.keys(activityFormItem.deal).length) {
          delete activityFormItem.dealId;

          if (dealPhotoFiles[activityIndex]) {
            activityFormItem.deal.mainPhoto = dealPhotoFiles[activityIndex];
          }
        } else {
          delete activityFormItem.deal;

          activityFormItem.dealId = activityFormItem.dealId || null;
        }

        if (activityFormItem.dealSource && Object.keys(activityFormItem.dealSource).length) {
          delete activityFormItem.dealSourceId;
        } else {
          delete activityFormItem.dealSource;

          activityFormItem.dealSourceId = activityFormItem.dealSourceId || null;
        }

        if (activityFormItem.dealContact && Object.keys(activityFormItem.dealContact).length) {
          activityFormItem.dealContact = { ...activityFormItem.dealContact };
          activityFormItem.dealContact.companyId = activityFormItem.dealContact.companyId || null;
          activityFormItem.dealContact.phone = activityFormItem.dealContact.phone
          && FormatService.isValidPhoneNumber(activityFormItem.dealContact.phone, true)
            ? FormatService.formatPhoneValue(activityFormItem.dealContact.phone) : null;
          activityFormItem.dealContact.groupIds = activityFormItem.dealContact.groupIds
          && activityFormItem.dealContact.groupIds.length ? activityFormItem.dealContact.groupIds.map((
            contactGroupItem: ContactGroup,
          ): number => contactGroupItem.id) : [];
        } else {
          delete activityFormItem.dealContact;
        }

        activityFormItem.sponsorId = activityFormItem.sponsorId || null;
        activityFormItem.brokerId = activityFormItem.brokerId || null;
        activityFormItem.lenderId = activityFormItem.lenderId || null;
        activityFormItem.dealContactIds = activityFormItem.dealContactIds && activityFormItem.dealContactIds.length
          ? activityFormItem.dealContactIds.map((contactItem: Contact): number => contactItem.id) : [];
        activityFormItem.amount = activityFormItem.amount
          ? FormatService.formatCleanNumber(activityFormItem.amount.toString()) : 0;
        activityFormItem.size = activityFormItem.size
          ? FormatService.formatCleanNumber(activityFormItem.size.toString()) : 0;
        activityFormItem.ltv = activityFormItem.ltv
          ? FormatService.formatCleanNumber(activityFormItem.ltv.toString(), true) : 0;
        activityFormItem.purchasePrice = activityFormItem.purchasePrice
          ? FormatService.formatCleanNumber(activityFormItem.purchasePrice.toString()) : 0;
        activityFormItem.capitalExpenditure = activityFormItem.capitalExpenditure
          ? FormatService.formatCleanNumber(activityFormItem.capitalExpenditure.toString()) : 0;
        activityFormItem.oneYearNoi = activityFormItem.oneYearNoi
          ? FormatService.formatCleanNumber(activityFormItem.oneYearNoi.toString()) : 0;
        activityFormItem.yieldOnCost = activityFormItem.yieldOnCost
          ? FormatService.formatCleanNumber(activityFormItem.yieldOnCost.toString(), true) : 0;
        activityFormItem.debtProceeds = activityFormItem.debtProceeds
          ? FormatService.formatCleanNumber(activityFormItem.debtProceeds.toString()) : 0;
        activityFormItem.requiredEquityAmount = activityFormItem.requiredEquityAmount
          ? FormatService.formatCleanNumber(activityFormItem.requiredEquityAmount.toString()) : 0;
        activityFormItem.interestLevel = activityFormItem.interestLevel || null;
        activityFormItem.ltc = activityFormItem.ltc
          ? FormatService.formatCleanNumber(activityFormItem.ltc.toString(), true) : 0;
        activityFormItem.equityProviderId = activityFormItem.equityProviderId || null;
        activityFormItem.investorId = activityFormItem.investorId || null;
        activityFormItem.additionalPartyId = activityFormItem.additionalPartyId || null;
        activityFormItem.tags = activityFormItem.tags && activityFormItem.tags.length
          ? activityFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];

        const activityOptions: ActivityFieldInput[] = [];
        if (activityFormItem.options && Object.keys(activityFormItem.options).length) {
          Object.keys(activityFormItem.options).forEach((activityFieldIndex: string): void => {
            activityOptions.push({
              ...activityFormItem.options[activityFieldIndex],
            });
          });
        }
        activityFormItem.options = activityOptions;

        const activityItem: ActivityInput = activityFormItem;

        activityList.push(activityItem);
      }
    });

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveActivityListData({
        activityListForm: {
          activityList,
        },
        ...getOptions(
          page,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        setInlineSaveActivityId(null);
        setInlineSaveIsConfirm(null);

        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSaveDisabled, isNewActivity, dealPhotoFiles, page, limit, portfolioIdsFilter, portfolioGroupIdsFilter,
    statusFilterStatus, queryCategory, sortParams, filters, inlineSaveActivityId, activityFieldNewItems,
    activityActiveIds, isActivityModalOpen, isEditMode, formKeyNew,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, isNewActivity, dealPhotoFiles, page, limit, portfolioIdsFilter, portfolioGroupIdsFilter,
    statusFilterStatus, queryCategory, sortParams, filters, inlineSaveActivityId, activityFieldNewItems,
    activityActiveIds, isActivityModalOpen, isEditMode, formKeyNew, initialFormValues, formValues, withFormErrors,
  ]);

  const onInlineSave: Function = useCallback((activityId: number): void => {
    setInlineSaveActivityId(activityId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onInlineCancel: Function = useCallback((activityId: number): void => {
    setInlineSaveActivityId(activityId);
    setInlineSaveIsConfirm(false);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const openStatusChangeModal: Function = useCallback((event: React.SyntheticEvent, activityIds: number[]): void => {
    setActivityActiveIds(activityIds || selectedRows);
    setIsStatusChangeModalOpen(true);
  }, [selectedRows]);

  const closeStatusModal: Function = useCallback((): void => {
    setStatusModalErrorMessage(null);
    setIsStatusChangeModalOpen(false);

    onCancelForm();
    closeMessage();
  }, [
    isNewActivity, inlineSaveActivityId, activityActiveIds, isEditMode, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const onStatusModalSaveClick: Function = useCallback((statusValue: string): Promise<ActionCallback> | null => {
    if (!activityActiveIds || !activityActiveIds.length || !statusValue) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveActivityStatusList({
        activityStatusListForm: {
          newStatus: statusValue,
          activityIds: activityActiveIds,
        },
        ...getOptions(
          page,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }, (response: ActionResponse): void => {
        setIsStatusChangeModalOpen(false);
        setStatusModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setStatusModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    activityActiveIds, page, limit, portfolioIdsFilter, portfolioGroupIdsFilter, isNewActivity, inlineSaveActivityId,
    statusFilterStatus, queryCategory, sortParams, filters, isEditMode, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const onTableColumnsChange: Function = useCallback((tableColumnSettings: TableField[], isDefault?: boolean): void => {
    setActivityTableColumnSettings(tableColumnSettings);

    if (!isDefault) {
      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_ACTIVITY_LIST_CAPITAL_RAISE,
        tableColumnSettings,
      }));
    }
  }, []);

  const openDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    activityId: number,
  ): void => {
    setActivityActiveIds(activityId ? [activityId] : selectedRows);
    setIsDeleteConfirmModalOpen(true);
  }, [selectedRows]);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setActivityActiveIds([]);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDealPhotoChange: Function = useCallback((activityId: number, file: File): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    setDealPhotoFiles({ ...dealPhotoFiles, [activityId]: file });
    changeForm(`activityListData[${formKeyActivity}].deal.mainPhoto`, file);
  }, [dealPhotoFiles]);

  const onDealPhotoDelete: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    const newDealPhotoFiles: Record<string, File> = dealPhotoFiles;
    delete newDealPhotoFiles[activityId];
    setDealPhotoFiles({ ...newDealPhotoFiles });

    changeForm(`activityListData[${formKeyActivity}].deal.mainPhoto`, null);
  }, [dealPhotoFiles]);

  const onOpenNewDealForm: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].deal`, {
      name: '',
    });

    setIsNewDealFormVisible(true);
  }, []);

  const onCloseNewDealForm: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].deal`, null);
    setIsNewDealFormVisible(false);
  }, []);

  const onOpenNewDealSourceForm: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealSource`, {
      name: null,
    });
    setIsNewDealSourceFormVisible(true);
  }, []);

  const onCloseNewDealSourceForm: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealSource`, null);
    setIsNewDealSourceFormVisible(false);
  }, []);

  const onOpenNewDealContactForm: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealContact`, {
      firstName: null,
      lastName: null,
    });
    setIsNewDealContactFormVisible(true);
  }, []);

  const onCloseNewDealContactForm: Function = useCallback((activityId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealContact`, null);
    setIsNewDealContactFormVisible(false);
  }, []);

  const onDealContactGroupsChange: Function = useCallback((
    activityId: number, contactGroupValues: ContactGroup[],
  ): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealContact.groupIds`, contactGroupValues);
  }, []);

  const onDealContactPhoneChange: Function = useCallback((
    activityId: number, phoneValue?: string,
  ): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`activityListData[${formKeyActivity}].dealContact.phone`, phoneValue);
  }, []);

  const openExportModal: Function = useCallback((): void => {
    if (selectedRows) {
      setActivityActiveIds(selectedRows);
      setIsExportModalOpen(true);
    }
  }, [selectedRows]);

  const closeExportModal: Function = useCallback((): void => {
    setIsExportModalOpen(false);

    onCancelForm();
    closeMessage();
  }, [
    isNewActivity, inlineSaveActivityId, activityActiveIds, isEditMode, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const onExportSuccess: Function = useCallback((): void => {
    onCancelForm();

    setStatusMessage('Exported successfully');
    setIsOpenMessage(true);
  }, [
    isNewActivity, inlineSaveActivityId, activityActiveIds, isEditMode, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const onImportSuccess: Function = useCallback((): void => {
    setPage(PAGE_DEFAULT);

    dispatch(getActivityListData({
      ...getOptions(
        PAGE_DEFAULT,
        limit,
        portfolioIdsFilter,
        portfolioGroupIdsFilter,
        statusFilterStatus,
        queryCategory,
        sortParams,
        filters,
      ),
    }));
  }, [limit, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, queryCategory, sortParams, filters]);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!activityActiveIds || !activityActiveIds.length) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteActivityList({
        activityIds: activityActiveIds,
        isListItem: true,
        ...getOptions(
          page,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    activityActiveIds, isNewActivity, inlineSaveActivityId, isEditMode, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const onCreateButtonClick: Function = useCallback((): void => {
    openActivityModal({}, false, true);
  }, []);

  const onActivityListCreateClick: Function = useCallback((): void => {
    openActivityListModal();
  }, []);

  const onActivityListChange: Function = useCallback((): void => {
    if (activityListModalErrorMessage) {
      setActivityListModalErrorMessage(null);
    }
  }, [activityListModalErrorMessage]);

  const onActivityListSaveForm: Function = useCallback((
    dealId: number, investorContactIds: number[], onSuccess?: Function,
  ): Promise<ActionCallback> | null => {
    if (!dealId || !investorContactIds || !investorContactIds.length) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveActivityList({
        activitySaveListForm: {
          dealId,
          investorContactIds,
        },
        ...getOptions(
          page,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }, (response: ActionResponse): void => {
        closeActivityListModal();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setActivityListModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    page, limit, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus, queryCategory, sortParams, filters,
  ]);

  const onActivityCreate: Function = useCallback((): void => {
    if (isEditMode) {
      setInlineSaveActivityId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(true);
    } else {
      onSubmitForm();
    }
  }, [
    isEditMode, isSaveDisabled, isNewActivity, dealPhotoFiles, page, limit, portfolioIdsFilter,
    portfolioGroupIdsFilter, statusFilterStatus, queryCategory, sortParams, filters, initialFormValues,
    inlineSaveActivityId, activityActiveIds, formValues, withFormErrors,
  ]);

  const onActivityModalCancel: MouseEventHandlerFunction = useCallback((): void => {
    if (isEditMode) {
      setInlineSaveActivityId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      closeActivityModal();
      onCancelForm();
    }
  }, [
    isEditMode, activityActiveIds, isNewActivity, inlineSaveActivityId, initialFormValues, dealPhotoFiles,
    activityFieldNewItems,
  ]);

  const onAddComment: Function = useCallback((
    activityId: number, comment: string, attachmentFiles: File[], mentionContactIds: number[],
    linkedEntities: CommentEntityInput[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachmentFiles, activityId, mentionContactIds, linkedEntities,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onUpdateComment: Function = useCallback((
    activityId: number, comment: string, attachments: AttachmentInput[], attachmentFiles: File[], commentId: number,
    mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachments, attachmentFiles, activityId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess(commentId);
      }
    }));
  }, []);

  const onDeleteComment: Function = useCallback((
    event: React.ChangeEvent, activityId: number, commentId: number,
  ): void => {
    dispatch(deleteActivityComment({
      activityId,
      commentId,
    }));

    event.stopPropagation();
  }, []);

  const onSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;
    setSelectedRows(isSelected ? activityListData.map((activityItem: Activity): number => activityItem.id) : []);
    setWithAllRowsSelected(!withAllRowsSelected);
  }, [activityListData, withAllRowsSelected]);

  const onSelectRow: Function = useCallback((rowId: number): void => {
    setSelectedRows(StructureService.selectRow(selectedRows, rowId));
    setWithAllRowsSelected(false);
  }, [selectedRows]);

  const onActivityFieldAddNewItem: Function = useCallback((activityFieldItem: ActivityFieldInput): void => {
    const activityFieldItems: ActivityFieldInput[] = activityFieldNewItems[activityItemId] || [];

    setActivityFieldNewItems({
      ...activityFieldNewItems,
      [activityItemId]: [...activityFieldItems, activityFieldItem],
    });
  }, [activityItemId, activityFieldNewItems]);

  const onActivityFieldDeleteClick: Function = useCallback((activityId: number, activityFieldId: number): void => {
    const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);
    const formKeyActivityField: string = FormService.getIndexedFormFieldKey(activityFieldId);

    if (activityFieldNewItems[activityId] && activityFieldNewItems[activityId].length) {
      activityFieldNewItems[activityId].forEach((
        activityFieldItem: ActivityFieldInput,
        activityFieldIndex: number,
      ): void => {
        if (activityFieldItem && activityFieldItem.id === activityFieldId) {
          delete activityFieldNewItems[activityId][activityFieldIndex];
        }
      });
      setActivityFieldNewItems({
        ...activityFieldNewItems,
        [activityId]: [...activityFieldNewItems[activityId]
          .filter((activityFieldItem: ActivityFieldInput): boolean => !!activityFieldItem)],
      });
    }

    changeForm(`activityListData[${formKeyActivity}].options[${formKeyActivityField}]`, null);
  }, [activityFieldNewItems]);

  const onLongClick: Function = useCallback((): void => {
    if (!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode) {
      dispatch(toggleSelectMode(true));
    }
  }, [isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onStatusFilterValueChange: Function = useCallback((statusValue?: string): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryPage) {
      queryParams[QUERY_PARAM_PAGE] = queryPage;
    }

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusValue) {
      queryParams[QUERY_PARAM_STATUS] = statusValue;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryPage, queryCategory, queryLimit, filters, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onBeforeStatusFilterValueChange: Function = useCallback((statusValue: string): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastStatusValue(statusValue || null);
      setLastPortfolioGroupIdsValue(undefined);
      setLastPortfolioIdsValue(undefined);
      setLastLocation(undefined);

      return false;
    }

    return true;
  }, [isFormChanged]);

  const onPortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds?: number[], portfolioFilterIds?: number[],
  ): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryPage) {
      queryParams[QUERY_PARAM_PAGE] = queryPage;
    }

    if (queryCategory) {
      queryParams[QUERY_PARAM_CATEGORY] = queryCategory;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    if (statusFilterStatus) {
      queryParams[QUERY_PARAM_STATUS] = statusFilterStatus;
    } else {
      delete queryParams[QUERY_PARAM_STATUS];
    }

    if (portfolioFilterIds && portfolioFilterIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioFilterIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioFilterGroupIds && portfolioFilterGroupIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioFilterGroupIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryPage, queryCategory, queryLimit, filters, statusFilterStatus]);

  const onBeforePortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds?: number[], portfolioFilterIds?: number[],
  ): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastStatusValue(undefined);
      setLastPortfolioGroupIdsValue(portfolioFilterGroupIds || []);
      setLastPortfolioIdsValue(portfolioFilterIds || []);
      setLastLocation(undefined);

      return false;
    }

    return true;
  }, [isFormChanged]);

  const applyUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...filters };

    if (queryFilterDealId) {
      filterValues[FILTER_TYPE_DEAL_ID] = queryFilterDealId;
    }

    if (queryFilterBrokerId) {
      filterValues[FILTER_TYPE_BROKER_ID] = queryFilterBrokerId;
    }

    if (queryFilterLenderId) {
      filterValues[FILTER_TYPE_LENDER_ID] = queryFilterLenderId;
    }

    if (queryFilterInvestorId) {
      filterValues[FILTER_TYPE_INVESTOR_ID] = queryFilterInvestorId;
    }

    if (queryFilterDealContactId) {
      filterValues[FILTER_TYPE_DEAL_CONTACT_ID] = queryFilterDealContactId;
    }

    if (queryFilterType) {
      filterValues[FILTER_TYPE_TYPE] = queryFilterType;
    }

    setFilters(filterValues);

    return filterValues;
  }, [
    filters, queryFilterDealId, queryFilterBrokerId, queryFilterLenderId,
    queryFilterInvestorId, queryFilterDealContactId, queryFilterType,
  ]);

  const setFilterValue: Function = useCallback((filterType: string, value: string): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...currentFilters, [filterType]: selectedValue };
    setCurrentFilters(newFilters);

    return newFilters;
  }, [currentFilters]);

  const onDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = setFilterValue(filterType, null);

    onApplyFilters(newFilters);
  }, [
    locationQueryParams, currentFilters, limit, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus,
    queryCategory, sortParams, queryCategory, queryLimit,
  ]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    dispatch(setStatusFilter(undefined));

    onCancelForm();
  }, [
    isNewActivity, inlineSaveActivityId, activityActiveIds, isEditMode, initialFormValues,
    dealPhotoFiles, activityFieldNewItems,
  ]);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getActivityListData({
      ...getOptions(
        page,
        limit,
        portfolioIdsFilter,
        portfolioGroupIdsFilter,
        statusFilterStatus,
        queryCategory,
        sortParams,
        filters,
      ),
    }));
  }, [
    page, limit, portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus,
    queryCategory, sortParams, filters,
  ]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (activityStatusList) {
      let initialStatusFilterText: string | null = null;
      activityStatusList.forEach((activityStatusItem: ActivityStatus): void => {
        if (activityStatusItem.name === statusFilterStatus) {
          initialStatusFilterText = activityStatusItem.title;
        }
      });
      setStatusFilterText(initialStatusFilterText);
    }
  }, [activityStatusList]);

  useEffect((): void => {
    if (activityCategoryList) {
      activityCategoryList.forEach((activityCategoryItem: ActivityCategory): void => {
        if (queryCategory && queryCategory === activityCategoryItem.name) {
          setCategoryText(activityCategoryItem.title);
        }
      });
    }
  }, [activityCategoryList]);

  useEffect((): void => {
    if (activityListData && activityItemData) {
      activityListData.forEach((activityItem: Activity): void => {
        if (activityItem.id === activityItemData.id) {
          setActivityItemData({ ...activityItem });
        }
      });
    }
  }, [newCommentList]);

  useEffect((): void => {
    if (currentCategory !== queryCategory) {
      setCurrentCategory(queryCategory);
      dispatch(resetActivityListData());
    }
  }, [queryCategory]);

  useEffect((): void => {
    if (!StructureService.checkIfArraysEqual(portfolioIdsFilter, queryFilterPortfolioIds || undefined)
      || !StructureService.checkIfArraysEqual(portfolioGroupIdsFilter, queryFilterPortfolioGroupIds || undefined)
      || statusFilterStatus !== queryFilterStatus) {
      updateQueryParams();
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus]);

  useEffect((): void => {
    if (!isDataLoaded) {
      const currentPage: number = queryPage ? queryPage - 1 : PAGE_DEFAULT;
      if (queryPage) {
        setPage(queryPage - 1);
      }

      const currentLimit: number = queryLimit || limit;
      if (queryLimit) {
        setLimit(queryLimit);
      }

      const filterValues: Record<string, FilterValue> = applyUrlFilters();
      if (queryFilterStatus) {
        dispatch(setStatusFilter(queryFilterStatus));
      }

      if (queryFilterPortfolioIds && queryFilterPortfolioIds.length) {
        dispatch(setPortfolioIdsFilter(queryFilterPortfolioIds));
      }

      if (queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length) {
        dispatch(setPortfolioGroupIdsFilter(queryFilterPortfolioGroupIds));

        if (!queryFilterPortfolioIds || !queryFilterPortfolioIds.length) {
          dispatch(setPortfolioIdsFilter(undefined));
        }
      }

      dispatch(getActivityListData({
        ...getOptions(
          currentPage,
          currentLimit,
          queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
          queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
            ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
          queryFilterStatus || statusFilterStatus,
          queryCategory,
          sortParams,
          filterValues,
        ),
      }));
    }
  }, [isDataLoaded, queryCategory]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getActivityListData({
        ...getOptions(
          page,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }));

      FormService.forceResetForm(resetForm, formProps);
    }
  }, [page, sortParams]);

  useEffect((): void => {
    if (isDataLoaded) {
      setPage(PAGE_DEFAULT);
      setFilters(DEFAULT_FILTERS);

      dispatch(getActivityListData({
        ...getOptions(
          PAGE_DEFAULT,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }));

      FormService.forceResetForm(resetForm, formProps);
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, statusFilterStatus]);

  useEffect((): void => {
    if (isDataLoaded) {
      setPage(PAGE_DEFAULT);

      dispatch(getActivityListData({
        ...getOptions(
          PAGE_DEFAULT,
          limit,
          portfolioIdsFilter,
          portfolioGroupIdsFilter,
          statusFilterStatus,
          queryCategory,
          sortParams,
          filters,
        ),
      }));

      FormService.forceResetForm(resetForm, formProps);
    }
  }, [limit]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastStatusValue || lastStatusValue === null) {
        dispatch(setStatusFilter(lastStatusValue));

        setLastStatusValue(undefined);
      }

      if (lastPortfolioIdsValue) {
        dispatch(setPortfolioIdsFilter(lastPortfolioIdsValue));

        setLastPortfolioIdsValue(undefined);
      }

      if (lastPortfolioGroupIdsValue) {
        dispatch(setPortfolioGroupIdsFilter(lastPortfolioGroupIdsValue));

        setLastPortfolioGroupIdsValue(undefined);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (inlineSaveIsConfirm !== null && inlineSaveActivityId !== null) {
      if (inlineSaveIsConfirm) {
        onSubmitForm();
      } else {
        onCancelForm();
      }
    }
  }, [inlineSaveIsConfirm]);

  useEffect((): void => {
    if (queryCreate) {
      openActivityModal({}, false, true);
      removeCreateQueryParam();
    }
  }, [queryCreate]);

  useEffect((): void => {
    if (withServerErrors && inlineSaveIsConfirm !== null) {
      setInlineSaveActivityId(null);
      setInlineSaveIsConfirm(null);
    }
  }, [withServerErrors]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!queryCategory || queryCategory !== ACTIVITY_CATEGORY_CAPITAL_RAISE) {
    const queryParams: LocationQueryParams = locationQueryParams || {};
    queryParams[QUERY_PARAM_CATEGORY] = ACTIVITY_CATEGORY_CAPITAL_RAISE;

    return <Navigate to={`${ROUTE_ACTIVITY_LIST}${FormatService.formatQueryParams(queryParams)}`} />;
  }

  if (!isDataLoaded || currentCategory !== queryCategory) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={activityListViewPermissions}>
      <Form id={FORM_ID_ACTIVITY_LIST} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader
            title={pageTitle}
            leftBlockComponent={isPortfolioFilterViewAllowed && !!portfolioGroupList && !isSmallScreen && (
              <PortfolioFilter
                isDisabled={isFormChanged}
                portfolioGroupList={portfolioGroupList}
                onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
                onBeforePortfolioFilterValuesChange={onBeforePortfolioFilterValuesChange}
              />
            )}
          />
          {
            isSmallScreen && (
              <CheckAccess permissions={activityPortfolioFilterViewPermissions}>
                <PortfolioFilter
                  isInline
                  isDisabled={isFormChanged}
                  portfolioGroupList={filteredPortfolioGroupList}
                  onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
                  onBeforePortfolioFilterValuesChange={onBeforePortfolioFilterValuesChange}
                />
              </CheckAccess>
            )
          }
          <CheckAccess permissions={activityStatusFilterViewPermissions}>
            <StatusFilter
              isDisabled={isFormChanged}
              statusFilterStatus={statusFilterStatus}
              statusFilterList={statusFilterList}
              onStatusFilterValueChange={onStatusFilterValueChange}
              onBeforeStatusFilterValueChange={onBeforeStatusFilterValueChange}
            />
          </CheckAccess>
          <Controls
            isTableExport
            withFilters
            withSelectAll
            withSelectMode
            withColumnSettings
            isSaveDisabled={isSaveDisabled}
            isExportDisabled={isExportDisabled}
            isCustomizable={isCustomizable}
            isCustomizeMode={isCustomizeMode}
            withAllFiltersEmpty={withAllFiltersEmpty}
            withAllRowsSelected={withAllRowsSelected}
            exportFormatList={FORMAT_LIST_EXPORT_ACTIVITY}
            importFormatList={FORMAT_LIST_IMPORT_ACTIVITY}
            importExportEntityType={IMPORT_EXPORT_ENTITY_TYPE_ACTIVITY_LIST}
            filters={filters}
            currentFilters={currentFilters}
            createButtonTitle={createButtonTitle}
            createAdditionalItemButtonTitle={createAdditionalItemButtonTitle}
            createPermission={ACTIVITY_CREATE}
            editPermissions={activityEditPermissions}
            importPermission={ACTIVITY_LIST_IMPORT}
            exportPermission={ACTIVITY_LIST_EXPORT}
            changeStatusPermission={ACTIVITY_STATUS_EDIT}
            deletePermission={ACTIVITY_DELETE}
            tableFields={activityListTableFieldLabels}
            tableFieldGroups={tableActivityListFieldGroups}
            tableColumnSettings={activityTableColumnSettings}
            rowsTotal={activityTotal}
            rowsSelectedTotal={selectedRows.length}
            selectedItemIds={selectedRows}
            portfolioIds={portfolioIdsFilter}
            portfolioGroupIds={portfolioGroupIdsFilter}
            status={statusFilterStatus}
            onApplyFilters={onApplyFilters}
            onResetFilters={onResetFilters}
            onCancelForm={onCancelForm}
            onSubmitForm={onSubmitForm}
            onExportClick={openExportModal}
            onDeleteClick={openDeleteConfirmModal}
            onImportSuccess={onImportSuccess}
            onSelectAllRows={onSelectAllRows}
            onTableColumnsChange={onTableColumnsChange}
            onStatusChangeClick={openStatusChangeModal}
            onCreateButtonClick={onCreateButtonClick}
            onCreateAdditionalItemButtonClick={onActivityListCreateClick}
            filterComponent={(
              <ActivityListFilters
                filters={filters}
                currentFilters={currentFilters}
                filterDealList={filterDealList}
                filterActivityTypeList={filterActivityTypeList}
                filterBrokerList={filterBrokerList}
                filterLenderList={filterLenderList}
                filterInvestorList={filterInvestorList}
                filterDealContactList={filterDealContactList}
                onSetFilterValue={setFilterValue}
              />
            )}
          />
          {
            !isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode && (
              <FilterPanel
                filters={filters}
                filterOptionMap={FILTER_OPTION_MAP}
                filterKeyMap={FILTER_KEY_MAP}
                filterLabelMap={FILTER_LABEL_MAP}
                filterLabelKeyMap={FILTER_LABEL_KEY_MAP}
                onDeleteFilterValue={onDeleteFilterValue}
              />
            )
          }
          <ActivityListInternal
            isSaving={isFormSaving}
            isSaveDisabled={isSaveDisabled}
            isStatusFilterVisible={isStatusFilterVisible}
            withAllRowsSelected={withAllRowsSelected}
            withAllFiltersEmpty={withAllFiltersEmpty}
            page={page}
            limit={limit}
            sortParams={sortParams}
            category={queryCategory}
            tableName={TABLE_NAME_ACTIVITY_LIST_CAPITAL_RAISE}
            selectedRows={selectedRows}
            tableColumnSettings={activityTableColumnSettings}
            activityActiveIds={activityActiveIds}
            inlineSaveActivityId={inlineSaveActivityId}
            activityListData={activityListData}
            activityTotal={activityTotal}
            activityListSummaryData={activityListSummaryData}
            activityStatusList={activityStatusList}
            activityCategoryList={activityCategoryList}
            measureUnitList={measureUnitList}
            activityInterestLevelList={activityInterestLevelList}
            activityTypeList={activityTypeList}
            dealList={dealList}
            contactList={contactList}
            contactCompanyList={contactCompanyList}
            contactPersonList={contactPersonList}
            contactPersonInternalList={contactPersonInternalList}
            tagList={tagList}
            onSelectRow={onSelectRow}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
            onSortParamsChange={onSortParamsChange}
            onTagsChange={onTagsChange}
            onDealContactsChange={onDealContactsChange}
            onTagClick={onTagClick}
            onLongClick={onLongClick}
            onTableColumnsChange={onTableColumnsChange}
            onOpenModal={openActivityModal}
            onOpenDeleteConfirmModal={openDeleteConfirmModal}
            onInlineSave={onInlineSave}
            onInlineCancel={onInlineCancel}
            onOpenStatusChangeModal={openStatusChangeModal}
            onInlineEditModeButtonClick={onInlineEditModeButtonClick}
            onActivityDealSearch={onActivityDealSearch}
            onActivityDealSearchClose={onActivityDealSearchClose}
            onContactSearch={onContactSearch}
            onContactSearchClose={onContactSearchClose}
            onContactPersonSearch={onContactPersonSearch}
            onContactPersonSearchClose={onContactPersonSearchClose}
            onContactPersonInternalSearch={onContactPersonInternalSearch}
            onContactPersonInternalSearchClose={onContactPersonInternalSearchClose}
            onContactCompanySearch={onContactCompanySearch}
            onContactCompanySearchClose={onContactCompanySearchClose}
            onTagSearch={onTagSearch}
            onTagSearchClose={onTagSearchClose}
          />
          {
            isFormSaving && isEditMode && (
              <Loader isInline isSubmit isFloating isHidden={isMobileScreen} />
            )
          }
          <ControlButtonBlock
            isExportMode={isExportMode}
            isSelectMode={isSelectMode}
            isEditMode={isEditMode}
            isSaveDisabled={isSaveDisabled}
            isExportDisabled={isExportDisabled}
            withOffset={isRightDrawerOpen}
            onCancelForm={onCancelForm}
            onSubmitForm={onSubmitForm}
            onExportClick={openExportModal}
          />
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <ActivityModal
          isOpen={isActivityModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isCommentSaving={isCommentSaving}
          isEditMode={isEditMode}
          isNewActivity={isNewActivity}
          isNewDealFormVisible={isNewDealFormVisible}
          isNewDealSourceFormVisible={isNewDealSourceFormVisible}
          isNewDealContactFormVisible={isNewDealContactFormVisible}
          isCommentFormActive={isCommentFormActive}
          withFixedFooter={isNewActivity}
          queryCommentId={queryCommentId}
          currentUser={currentUser}
          activityData={activityItemData}
          activityFieldNewItems={activityFieldNewItems[activityItemId]}
          dealPhotoFile={dealPhotoFiles[activityItemId]}
          newCommentList={newCommentList}
          dealList={dealList}
          assetList={assetList}
          contactList={contactList}
          contactCompanyList={contactCompanyList}
          contactPersonList={contactPersonList}
          contactPersonInternalList={contactPersonInternalList}
          contactGroupList={contactGroupList}
          contactLinkedUserList={contactLinkedUserList}
          measureUnitList={measureUnitList}
          tagList={tagList}
          activityTypeList={activityTypeList}
          activityStatusList={activityStatusList}
          activityInterestLevelList={activityInterestLevelList}
          saveFormDeps={saveFormDeps}
          onCreate={onActivityCreate}
          onCancel={onActivityModalCancel}
          onCloseModal={closeActivityModal}
          onSubmitCreateForm={onSubmitCreateForm}
          onAddComment={onAddComment}
          onUpdateComment={onUpdateComment}
          onDeleteComment={onDeleteComment}
          onDealPhotoChange={onDealPhotoChange}
          onDealPhotoDelete={onDealPhotoDelete}
          onOpenNewDealForm={onOpenNewDealForm}
          onCloseNewDealForm={onCloseNewDealForm}
          onOpenNewDealSourceForm={onOpenNewDealSourceForm}
          onCloseNewDealSourceForm={onCloseNewDealSourceForm}
          onOpenNewDealContactForm={onOpenNewDealContactForm}
          onCloseNewDealContactForm={onCloseNewDealContactForm}
          onDealContactGroupsChange={onDealContactGroupsChange}
          onDealContactPhoneChange={onDealContactPhoneChange}
          onFieldDeleteClick={onActivityFieldDeleteClick}
          onAddNewFieldItem={onActivityFieldAddNewItem}
          onTagsChange={onTagsChange}
          onDealContactsChange={onDealContactsChange}
          onActivityDealSearch={onActivityDealSearch}
          onActivityDealSearchClose={onActivityDealSearchClose}
          onDealAssetSearch={onDealAssetSearch}
          onDealAssetSearchClose={onDealAssetSearchClose}
          onContactSearch={onContactSearch}
          onContactSearchClose={onContactSearchClose}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
        />
        <ActivityListModal
          isOpen={isActivityListModalOpen}
          isSaving={isFormSaving}
          errorMessage={activityListModalErrorMessage}
          dealList={dealList}
          contactList={contactList}
          saveFormDeps={saveFormDeps}
          onChange={onActivityListChange}
          onSaveForm={onActivityListSaveForm}
          onCloseModal={closeActivityListModal}
          onContactSearch={onContactSearch}
          onContactSearchClose={onContactSearchClose}
          onActivityDealSearch={onActivityDealSearch}
          onActivityDealSearchClose={onActivityDealSearchClose}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          withExportAll={withAllRowsSelected}
          entityIds={activityActiveIds}
          importExportEntityType={IMPORT_EXPORT_ENTITY_TYPE_ACTIVITY_LIST}
          exportFormatList={FORMAT_LIST_EXPORT_ACTIVITY}
          portfolioIds={portfolioIdsFilter}
          portfolioGroupIds={portfolioGroupIdsFilter}
          status={statusFilterStatus}
          onSuccess={onExportSuccess}
          onCloseModal={closeExportModal}
        />
        <StatusModal
          isOpen={isStatusChangeModalOpen}
          isSaving={isFormSaving}
          errorMessage={statusModalErrorMessage}
          itemActiveIds={activityActiveIds}
          statusList={activityStatusList}
          onSaveForm={onStatusModalSaveClick}
          onCloseModal={closeStatusModal}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          errorMessage={confirmModalErrorMessage}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    activityList,
    statusFilter,
    portfolioFilter,
    controls,
    activity,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  const activityListData: Activity[] = activityList.activityListData && activityList.activityListData.activityList;

  if (activityListData) {
    initialValues.activityListData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        status: ACTIVITY_STATUS_INITIAL,
        type: ACTIVITY_TYPE_DEBT,
        category: ACTIVITY_CATEGORY_CAPITAL_RAISE,
        dealId: null,
        deal: {},
        dealSource: {},
        dealContact: {},
      },
    };

    activityListData.forEach((activityItem: Activity): void => {
      const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityItem.id);

      initialValues.activityListData[formKeyActivity] = {
        id: activityItem.id,
        status: activityItem.status || ACTIVITY_STATUS_INITIAL,
        type: activityItem.type || ACTIVITY_TYPE_DEBT,
        category: activityItem.category,
        dealId: activityItem.deal ? activityItem.deal.id : null,
        dealSourceId: activityItem.dealSource ? activityItem.dealSource.id : null,
        sponsorId: activityItem.sponsor ? activityItem.sponsor.id : null,
        brokerId: activityItem.broker ? activityItem.broker.id : null,
        lenderId: activityItem.lender ? activityItem.lender.id : null,
        dealContactIds: activityItem.dealContacts && activityItem.dealContacts.length
          ? activityItem.dealContacts : [],
        amount: FormatService.formatNumber(activityItem.amount),
        pricing: activityItem.pricing,
        size: FormatService.formatNumber(activityItem.size),
        sizeType: activityItem.sizeType ? activityItem.sizeType.name : null,
        ltv: FormatService.formatNumber(activityItem.ltv, true),
        purchasePrice: FormatService.formatNumber(activityItem.purchasePrice),
        capitalExpenditure: FormatService.formatNumber(activityItem.capitalExpenditure),
        oneYearNoi: FormatService.formatNumber(activityItem.oneYearNoi),
        yieldOnCost: FormatService.formatNumber(activityItem.yieldOnCost, true),
        debtProceeds: FormatService.formatNumber(activityItem.debtProceeds),
        requiredEquityAmount: FormatService.formatNumber(activityItem.requiredEquityAmount),
        interestLevel: activityItem.interestLevel,
        ltc: FormatService.formatNumber(activityItem.ltc, true),
        equityProviderId: activityItem.equityProvider ? activityItem.equityProvider.id : null,
        investorId: activityItem.investor ? activityItem.investor.id : null,
        additionalPartyId: activityItem.additionalParty ? activityItem.additionalParty.id : null,
        additionalPartyDetails: activityItem.additionalPartyDetails,
        lenderRole: activityItem.lenderRole,
        maxAmount: activityItem.maxAmount,
        loanRecourse: activityItem.loanRecourse,
        tags: activityItem.tags,
        options: {},
        deal: {},
        dealSource: {},
        dealContact: {},
      };

      if (activityItem.options && activityItem.options.length) {
        activityItem.options.forEach((activityFieldItem: ActivityField): void => {
          const formKeyActivityField: string = FormService.getIndexedFormFieldKey(activityFieldItem.id);

          initialValues.activityListData[formKeyActivity].options[formKeyActivityField] = {
            name: activityFieldItem.name,
            value: activityFieldItem.value,
          };
        });
      }
    });
  }

  return {
    isDataLoaded: !!activityList.activityListData,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isCommentSaving: activity.isCommentSaving,
    isFormSaving: activityList.isFormSaving,
    statusFilterStatus: statusFilter.statusFilter,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
    isExportMode: controls.isExportMode,
    isSelectMode: controls.isSelectMode,
    isEditMode: controls.isEditMode,
    isCustomizeMode: controls.isCustomizeMode,
    currentUser: app.baseData && app.baseData.currentUser,
    newCommentList: activityList.commentList,
    portfolioGroupList: activityList.activityListData && activityList.activityListData.portfolioGroupList,
    activityListData: activityList.activityListData && activityList.activityListData.activityList,
    activityTotal: activityList.activityListData && activityList.activityListData.activityTotal,
    activityListSummaryData: activityList.activityListData && activityList.activityListData.activityListSummaryData,
    activityStatusList: activityList.activityListData && activityList.activityListData.activityStatusList,
    activityCategoryList: activityList.activityListData && activityList.activityListData.activityCategoryList,
    dealList: activityList.activityListData && activityList.activityListData.dealList,
    contactList: activityList.activityListData && activityList.activityListData.contactList,
    contactCompanyList: activityList.activityListData && activityList.activityListData.contactCompanyList,
    contactPersonList: activityList.activityListData && activityList.activityListData.contactPersonList,
    contactPersonInternalList: activityList.activityListData && activityList.activityListData.contactPersonInternalList,
    contactGroupList: activityList.activityListData && activityList.activityListData.contactGroupList,
    measureUnitList: activityList.activityListData && activityList.activityListData.measureUnitList,
    activityInterestLevelList: activityList.activityListData && activityList.activityListData.activityInterestLevelList,
    activityTypeList: activityList.activityListData && activityList.activityListData.activityTypeList,
    assetList: activityList.activityListData && activityList.activityListData.assetList,
    tagList: activityList.activityListData && activityList.activityListData.tagList,
    contactLinkedUserList: activityList.activityListData && activityList.activityListData.contactLinkedUserList,
    filterDealList: activityList.activityListData && activityList.activityListData.activityListFilters
      && activityList.activityListData.activityListFilters.dealList,
    filterActivityTypeList: activityList.activityListData && activityList.activityListData.activityListFilters
      && activityList.activityListData.activityListFilters.activityTypeList,
    filterBrokerList: activityList.activityListData && activityList.activityListData.activityListFilters
      && activityList.activityListData.activityListFilters.brokerList,
    filterLenderList: activityList.activityListData && activityList.activityListData.activityListFilters
      && activityList.activityListData.activityListFilters.lenderList,
    filterInvestorList: activityList.activityListData && activityList.activityListData.activityListFilters
      && activityList.activityListData.activityListFilters.investorList,
    filterDealContactList: activityList.activityListData && activityList.activityListData.activityListFilters
      && activityList.activityListData.activityListFilters.dealContactList,
  };
};

const ActivityListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ activityList }: Reducers): ValidationErrorsMap => (
    activityList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ activityList }: Reducers): ValidationErrorsMap => (
    activityList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_ACTIVITY_LIST}
      component={ActivityList}
      validator={ActivityListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(ActivityListForm);
